import { useForm, Controller } from 'react-hook-form';
import { ktmToday } from '../../utils/calendar';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import DualDatePicker from '../common/DualDatePicker';
import EditDetailsModal, { Field, inputCls } from '../common/EditDetailsModal';
import { updateAdminDetails } from '../../services/adminService';
import { sanitizePhone, isValidPhone, PHONE_HINT } from '../../utils/inputRules';

const isoDay = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');

// Admin "Edit Details" for an admin account. Editing another admin needs the Primary Admin (also
// enforced server-side); editing your own row is always allowed. Login email is Phase 2.
export default function EditAdminModal({ admin, onClose, onSaved }) {
  const qc = useQueryClient();
  const hasLogin = !!admin.email;

  const { register, control, handleSubmit, setValue, formState: { errors } } = useForm({
    defaultValues: {
      name:        admin.name || '',
      phone:       admin.phone || '',
      address:     admin.address || '',
      dateOfBirth: isoDay(admin.dateOfBirth),
    },
  });

  const mutation = useMutation({
    mutationFn: (payload) => updateAdminDetails(admin._id, payload),
    onSuccess: (res) => {
      toast.success('Details updated');
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      onSaved?.(res?.data?.user);
      onClose?.();
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to update details'),
  });

  const onSubmit = (vals) => {
    mutation.mutate({
      name:        vals.name?.trim(),
      phone:       vals.phone?.trim() || '',
      address:     vals.address?.trim() || '',
      dateOfBirth: vals.dateOfBirth || '',
    });
  };

  return (
    <EditDetailsModal
      title="Edit Admin"
      subtitle="Update this admin's personal details."
      onClose={onClose}
      onSubmit={handleSubmit(onSubmit)}
      saving={mutation.isPending}
      hasLogin={hasLogin}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Name" required error={errors.name?.message}>
          <input className={inputCls(errors.name)}
            {...register('name', { required: 'Name is required', maxLength: { value: 50, message: 'At most 50 characters' } })} />
        </Field>
        <Field label="Phone" error={errors.phone?.message}>
          <input type="tel" placeholder="97/98XXXXXXXX" className={inputCls(errors.phone)}
            {...register('phone', { validate: (val) => !val || isValidPhone(val) || PHONE_HINT })}
            onChange={(e) => setValue('phone', sanitizePhone(e.target.value), { shouldValidate: true })} />
        </Field>
        <Controller name="dateOfBirth" control={control}
          render={({ field }) => (
            <DualDatePicker label="Date of Birth" value={field.value || ''} onChange={field.onChange}
              max={ktmToday()} fastNav />
          )} />
      </div>
      <Field label="Address">
        <input placeholder="Street, city, district" className={inputCls()} {...register('address', { maxLength: { value: 300, message: 'At most 300 characters' } })} />
      </Field>
    </EditDetailsModal>
  );
}
